import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import {
  ComplianceTimeSeriesPoint,
  ComplianceTimeSeriesRequest,
  PartsGraphDataPoint,
  PartsGraphRequest,
  UpcomingActivityRequest,
  UpcomingActivityResponse
} from '../models/dashboard.model';
import { AccountDateFilterRequest } from '../../../_shared/models/account-date-filter-request.model';
import { Response } from '../../../_shared/models/response.model';
import { Pagination } from '../../../_shared/models/pagination-response.model';
import { SummaryRequest } from '../../../_shared/models';
import { KpiStatsResponse } from '../../../_shared/models/kpi-response.model';
import { MaintenanceInsightsResponse } from '../../maintenance/maintenance-dashboard/models/maintenance-dashboard-response.model';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private httpClient = inject(HttpClient);

  async getMaintenanceRequiredStats(request: SummaryRequest, cancelSubject: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/dashboard/required-maintenance`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getTotalMaintenanceCostStats(request: SummaryRequest, cancelSubject: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/dashboard/total-maintenance-cost`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getAvailableAssetsStats(request: SummaryRequest, cancelSubject: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/dashboard/available-assets`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getCompletedWorkOrdersStats(request: SummaryRequest, cancelSubject: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/dashboard/completed-work-orders`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getAverageWorkshopDowntimeStats(request: SummaryRequest, cancelSubject: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/dashboard/average-workshop-downtime`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getUpcomingActivities(request: UpcomingActivityRequest, cancelSubject: Subject<void>): Promise<Response<Pagination<UpcomingActivityResponse[]>>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/dashboard/upcoming-activity-detail`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getAssetComplianceTimeSeries(request: ComplianceTimeSeriesRequest, cancelSubject: Subject<void>): Promise<Response<ComplianceTimeSeriesPoint[]>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/dashboard/asset-compliance-time-series`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getCompletedWorkOrdersRatio(request: AccountDateFilterRequest, cancelSubject: Subject<void>): Promise<Response<MaintenanceInsightsResponse[]>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/dashboard/completed-work-orders-ratio`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getGlobalCompletedWorkOrdersRatio(request: AccountDateFilterRequest, cancelSubject: Subject<void>): Promise<Response<MaintenanceInsightsResponse[]>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/dashboard/global-completed-work-orders-ratio`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getPreventiveMaintenanceCostRatio(request: AccountDateFilterRequest, cancelSubject: Subject<void>): Promise<Response<MaintenanceInsightsResponse[]>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/preventive-maintenance-cost-ratio`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getCorrectiveMaintenanceCostRatio(request: AccountDateFilterRequest, cancelSubject: Subject<void>): Promise<Response<MaintenanceInsightsResponse[]>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/corrective-maintenance-cost-ratio`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getPartsByCategoryGraph(request: PartsGraphRequest, cancelSubject: Subject<void>): Promise<Response<PartsGraphDataPoint[]>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/parts/graph/parts-by-category`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getPartsStockStatusGraph(request: PartsGraphRequest, cancelSubject: Subject<void>): Promise<Response<PartsGraphDataPoint[]>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/parts/graph/stock-status`, request).pipe(takeUntil(cancelSubject))
    );
  }
}